import { XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import FirebaseStorageImage from '@/components/molecules/firebase-storage-image'
import ImageThumbnail from '@/components/molecules/image-thumbnail'
import ImageUploadControls from '@/components/molecules/image-upload-controls'
import type { StoredImage } from './checkpoint-question.utils'
import { ImagePreviewDialog } from './image-preview-dialog'

type PhotoCaptureProps = {
  photos: File[]
  onChange(photos: File[]): void
  /** Already-uploaded images shown when editing an existing response. */
  existingImages?: StoredImage[]
  onRemoveExisting?(image: StoredImage): void
}

export const PhotoCapture = ({
  photos,
  onChange,
  existingImages = [],
  onRemoveExisting,
}: PhotoCaptureProps) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const previewsRef = useRef(new Map<File, string>())
  const [, force] = useState({})

  useEffect(() => {
    const map = previewsRef.current
    const active = new Set(photos)
    for (const file of Array.from(active)) {
      if (!map.has(file)) {
        map.set(file, URL.createObjectURL(file))
      }
    }
    for (const [file, url] of Array.from(map.entries())) {
      if (!active.has(file)) {
        URL.revokeObjectURL(url)
        map.delete(file)
      }
    }
    force({})
  }, [photos])

  useEffect(() => {
    return () => {
      previewsRef.current.forEach(url => URL.revokeObjectURL(url))
      previewsRef.current.clear()
    }
  }, [])

  const onNewPhoto = (files: File[]) => {
    if (files.length === 0) return
    onChange([...photos, ...files])
  }

  const removePhoto = (file: File) => {
    onChange(photos.filter(p => p !== file))
  }

  return (
    <div className="flex flex-col gap-2">
      {(existingImages.length > 0 || photos.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {existingImages.map(image => (
            <div key={image.path}>
              <ImageThumbnail
                storagePath={image.path}
                name={image.name ?? ''}
                onClick={() => setPreviewOpen(true)}
                overlay={
                  onRemoveExisting ? (
                    <RemoveOverlay onRemove={() => onRemoveExisting(image)} />
                  ) : undefined
                }
              />
              <ImagePreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                title={image.name ?? ''}
              >
                <FirebaseStorageImage
                  alt={image.name ?? ''}
                  className="max-w-full max-h-[80vh] object-contain rounded"
                  storagePath={image.path}
                />
              </ImagePreviewDialog>
            </div>
          ))}
          {photos.map(file => {
            const src = previewsRef.current.get(file)
            return (
              <div key={src ?? file.name}>
                <ImageThumbnail
                  src={src}
                  name={file.name ?? ''}
                  onClick={() => setPreviewOpen(true)}
                  overlay={<RemoveOverlay onRemove={() => removePhoto(file)} />}
                />
                <ImagePreviewDialog
                  open={previewOpen}
                  onOpenChange={setPreviewOpen}
                  title={file.name}
                >
                  <img
                    src={src}
                    alt={file.name ?? ''}
                    className="max-w-full max-h-[80vh] object-contain rounded"
                  />
                </ImagePreviewDialog>
              </div>
            )
          })}
        </div>
      )}

      <ImageUploadControls onUpload={onNewPhoto} />
    </div>
  )
}

const RemoveOverlay = ({ onRemove }: { onRemove(): void }) => (
  <span
    role="button"
    tabIndex={-1}
    onClick={e => {
      e.stopPropagation()
      onRemove()
    }}
    className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5"
  >
    <XIcon className="size-3" />
  </span>
)
